// # Initialize Firebase configuration and services
import { initializeApp } from 'firebase/app';
import { getAnalytics } from 'firebase/analytics';
import { getAuth, connectAuthEmulator, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getStorage } from 'firebase/storage';
import { doc, getDoc } from 'firebase/firestore';

// # Firebase configuration object using environment variables
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,  // # Use environment variable
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,  // # Use environment variable
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,  // # Use environment variable
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,  // # Use environment variable
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,  // # Use environment variable
  appId: process.env.REACT_APP_FIREBASE_APP_ID,  // # Use environment variable
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID  // # Use environment variable
};

// # Initialize Firebase app
let app;
let analytics = null;
let auth;
let db;
let functions;
let storage;

try {
  app = initializeApp(firebaseConfig);
  analytics = process.env.NODE_ENV === 'production' ? getAnalytics(app) : null;
  auth = getAuth(app);
  
  // # Set persistence to local
  setPersistence(auth, browserLocalPersistence)
    .then(() => {
      // Initialize auth state
      const currentUser = auth.currentUser;
      if (currentUser) {
        currentUser.getIdToken(true); // Force token refresh
      }
    })
    .catch((error) => {
      console.error('Error setting auth persistence:', error);
    });

  db = getFirestore(app);
  functions = getFunctions(app, 'europe-west1');
  storage = getStorage(app);

  // # Connect to emulators in development
  if (process.env.NODE_ENV === 'development') {
    // # Use emulators if running locally
    connectAuthEmulator(auth, 'http://localhost:9099');
    connectFirestoreEmulator(db, 'localhost', 8080);
    connectFunctionsEmulator(functions, 'localhost', 5001);
    
    // # Emulators are enabled in development mode
  }
} catch (error) {
  console.error('Firebase initialization error:', error);
  throw error;
}

// # Update the username availability check function
export const checkUsernameAvailability = async (username) => {
  try {
    // Get current year and month for document ID
    const date = new Date();
    const docId = `${date.getFullYear()}_${(date.getMonth() + 1).toString().padStart(2, '0')}`;
    
    const uniqueRef = doc(db, 'unique', docId);
    const uniqueDoc = await getDoc(uniqueRef);

    if (!uniqueDoc.exists()) {
      // First username for this month
      return true;
    }

    // Check if username exists in the usernames array
    const usernames = uniqueDoc.data().usernames || [];
    return !usernames.includes(username.toLowerCase());
  } catch (error) {
    console.error('Error checking username:', error);
    throw error;
  }
};

export { app, analytics, auth, db, functions, storage }; 